import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import ChatMessage from './ChatMessage';
import AdBanner from './AdBanner';
import ImpreziaAdBanner from './ImpreziaAdBanner';
import Carousel from './Carousel';
import { faLocationDot, faVolumeUp, faVolumeMute, faRobot, faMicrophone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import VoiceService from './services/VoiceService';
import ReactMarkdown from 'react-markdown';
import Imprezia from '@adimpress/imprezia-sdk';

import {
  faArrowUp,
  faTriangleExclamation,
  faSpinner,
  faHome,
} from '@fortawesome/free-solid-svg-icons';
import './Chatbot.css';
import locationService from './services/LocationService';
import userProfileService from './services/UserProfileService';
import LangflowApiService from './services/LangflowApiService';
import VoiceHelper from './VoiceHelper';
import GoogleSpeechToText from './GoogleSpeechToText';
import KtmbAiAgent from './ktmb/KtmbAiAgent';
import KtmbDelays from './ktmb/KtmbDelays';

// Load ASO banner script once
if (typeof window !== 'undefined' && !window.__asoBannerLoaded) {
  const s = document.createElement('script');
  s.src = 'https://media.aso1.net/js/code.min.js';
  s.async = true;
  s.onload = () => {
    if (window.aso && typeof window.aso.load === 'function') {
      window.aso.load();
    }
  };
  s.onload = () => {
    if (window.aso && typeof window.aso.load === 'function') {
      window.aso.load();
    }
  };
  document.head.appendChild(s);
  window.__asoBannerLoaded = true;
}

// Legacy browser speech recognition (fallback)
const BrowserSpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
let browserRecognition;
if (BrowserSpeechRecognition) {
  try {
    browserRecognition = new BrowserSpeechRecognition();
    browserRecognition.lang = 'en-US';
    browserRecognition.continuous = false;
    browserRecognition.interimResults = false;
    console.log('Browser speech recognition initialized as fallback');
  } catch (error) {
    console.error('Error initializing browser speech recognition:', error);
  }
}

const generateSessionId = (email, userId) => {
  const now = new Date();
  const pad = (n) => n.toString().padStart(2, '0');
  const timestamp =
    now.getFullYear().toString() +
    pad(now.getMonth() + 1) +
    pad(now.getDate()) +
    pad(now.getHours()) +
    pad(now.getMinutes()) +
    pad(now.getSeconds());
  return `user_${email}_${userId}_${timestamp}`;
};

// Check for browser compatibility with speech recognition
const isAndroid = /android/i.test(navigator.userAgent) && !/iphone|ipad|ipod/i.test(navigator.userAgent);
const isChrome = /chrome|crios/i.test(navigator.userAgent) && !/edge|edg|opr/i.test(navigator.userAgent);
const isSafari = /safari/i.test(navigator.userAgent) && !/chrome|crios/i.test(navigator.userAgent);

// More accurate detection for Android WebView
const isAndroidWebView = 
  (/wv/.test(navigator.userAgent) && /android/i.test(navigator.userAgent)) || 
  (/Android/.test(navigator.userAgent) && /Version\//.test(navigator.userAgent)) || 
  (/Android/.test(navigator.userAgent) && /Chrome\/[0-9]/.test(navigator.userAgent) && window.navigator.vendor !== "Google Inc.") ||
  (/Android/.test(navigator.userAgent) && typeof window.ReactNativeWebView !== 'undefined');
  
// TTS service URLs
const PREMIUM_TTS_URL = 'https://api.streamelements.com/kappa/v2/speech';
// Premium male voices from StreamElements (excluding Brian)
const PREMIUM_MALE_VOICES = [
  'Joey',     // Default - Natural, friendly male voice
  'Matthew',  // Smooth, warm male voice
  'Justin',   // Clear, confident male voice
  'Russell',  // Authoritative male voice
  'Kevin'     // Warm, engaging male voice
];
// Free TTS endpoints that don't require API keys (fallbacks)
const FREE_TTS_ENDPOINTS = [
  'https://translate.google.com/translate_tts?ie=UTF-8&client=tw-ob&tl=en&q='
];
// Supported languages for the chatbot speech recognition
const SUPPORTED_LANGUAGES = {
  en: { name: 'English', code: 'en-US' },
  zh: { name: '中文 (Chinese)', code: 'zh-CN' }, // Covers Simplified and Traditional for TTS, uses Mandarin for recognition
  ms: { name: 'Bahasa Melayu (Malay)', code: 'ms-MY' },
  ur: { name: 'اردو (Urdu)', code: 'ur-PK' }
};

const isFirefox = /firefox/i.test(navigator.userAgent);
const isEdge = /edge|edg/i.test(navigator.userAgent);

// Speech recognition is supported in Chrome, Edge, Safari (newer versions), and Firefox
// We now use Google Cloud API as primary and browser recognition as fallback
const isSpeechRecognitionSupported = true; // Always true since we use Google Cloud API

console.log("Browser detection:", { 
  isChrome, 
  isAndroid, 
  isSafari, 
  isFirefox, 
  isEdge, 
  isSpeechRecognitionSupported,
  userAgent: navigator.userAgent 
});

const WEBVIEW_ERROR_PATTERNS = [
  'onReceivedError',
  'ERR_BLOCKED_BY_ORB',
  'net::ERR_',
  'failing',
  'error',
  'fail',
  'blocked',
  'denied',
  'refused'
];

// Ad zone IDs to rotate
// For debug: test rendering of a single zone only
const AD_ZONE_IDS = ['152464','152465','152466','152467','152468','152469','152470','152471'];

function isWebViewErrorMessage(msg) {
  if (!msg || !msg.text) return false;
  const text = msg.text.toLowerCase();
  return WEBVIEW_ERROR_PATTERNS.some(pattern => text.includes(pattern.toLowerCase()));
}

const Chatbot = () => {
  // Initialize Google Speech-to-Text hook
  const googleSpeech = GoogleSpeechToText();
  
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [voiceReplyEnabled, setVoiceReplyEnabled] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  const audioUnlockRef = useRef(null);
  
  // Speech synthesis state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechSynthesisSupported, setSpeechSynthesisSupported] = useState(false);
  const [hasSentWelcome, setHasSentWelcome] = useState(false);
  const [suggestedText, setSuggestedText] = useState('');
  const [messages, setMessages] = useState([]);
  const [conversationLanguage, setConversationLanguage] = useState('en'); // Track language of conversation, default to English
  const chatBoxRef = useRef(null);
  const endOfMessagesRef = useRef(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [showMapOverlay, setShowMapOverlay] = useState(false);
  const [showBlogOverlay, setShowBlogOverlay] = useState(false);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [locationLabel, setLocationLabel] = useState(''); // empty until resolved
  const isInitializedRef = useRef(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [showKtmbTimetable, setShowKtmbTimetable] = useState(false);
  const [showTimetablePopup, setShowTimetablePopup] = useState(false);
  const timetablePopupTimeout = useRef(null);
  const timetablePopupInterval = useRef(null);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [ktmbDelayInfo, setKtmbDelayInfo] = useState(null);
  const [ktmbDelayLoading, setKtmbDelayLoading] = useState(false);
  const [ktmbDelayError, setKtmbDelayError] = useState(null);
  const [ktmbDelays, setKtmbDelays] = useState([]);
  const [ktmbDelaysMessage, setKtmbDelaysMessage] = useState('');
  const [ktmbDelaysLoading, setKtmbDelaysLoading] = useState(false);
  const [ktmbDelaysError, setKtmbDelaysError] = useState(null);

  useEffect(() => {
    if (!userProfile) return; // Only run when userProfile is available
    
    let isMounted = true;
    const fetchDelay = async () => {
      setKtmbDelayLoading(true);
      setKtmbDelayError(null);
      try {
        const sessionId = userProfile.sessionId || userProfileService.getSessionId();
        const flowId = null; // or your default flowId
        // const info = await LangflowApiService.getKtmbDelayInfo(sessionId, flowId);
        const info = await LangflowApiService.getKtmbDelay();
        if (isMounted) setKtmbDelayInfo(info);
      } catch (err) {
        if (isMounted) setKtmbDelayError('Unable to fetch KTMB delay info.');
      } finally {
        if (isMounted) setKtmbDelayLoading(false);
      }
    };
    fetchDelay();
    return () => { isMounted = false; };
  }, [userProfile]);

  useEffect(() => {
    setKtmbDelaysLoading(true);
    LangflowApiService.getKtmbDelay()
      .then(data => {
        if (data && data.status === 'success' && Array.isArray(data.delays)) {
          setKtmbDelays(data.delays);
          setKtmbDelaysMessage(data.message || '');
        } else {
          setKtmbDelays([]);
          setKtmbDelaysMessage('');
          setKtmbDelaysError('No delay data found.');
        }
      })
      .catch(() => setKtmbDelaysError('Failed to fetch KTMB delays.'))
      .finally(() => setKtmbDelaysLoading(false));
  }, []);

  
  

  // Use useLayoutEffect for immediate scroll after render
  useLayoutEffect(() => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Effect to unlock audio context on the first user interaction
  useEffect(() => {
    // Create a silent audio element once
    if (!audioUnlockRef.current) {
      const silentAudio = new Audio(
        'data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhIAAAAAA='
      );
      silentAudio.volume = 0;
      silentAudio.loop = false;
      audioUnlockRef.current = silentAudio;
    }

    const unlockAudio = async () => {
      if (hasUserInteracted && !isAudioUnlocked && audioUnlockRef.current) {
        try {
          await audioUnlockRef.current.play();
          setIsAudioUnlocked(true);
          console.log('Audio context unlocked successfully.');
        } catch (error) {
          console.error('Failed to unlock audio context:', error);
          // Even if it fails, we mark it as attempted to avoid retries
          setIsAudioUnlocked(true);
        }
      }
    };

    unlockAudio();
  }, [hasUserInteracted, isAudioUnlocked]);

  const initializeLocation = async () => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const testMode = urlParams.get('testMode') === 'true';
      const userData = urlParams.get('userData');
      const reqSignature  = urlParams.get('requestSignature');

      const name = urlParams.get('name') || 'John';
      
      const email = urlParams.get('email') || 'john.doe@example.com';
      const userId = urlParams.get('userId') || 'testuser';

      let profile = null;
      let location = null;

      setIsProfileLoading(true);

      if (testMode) {
        // Real-time location in test mode
        setIsVoiceEnabled(true);
        location = await locationService.updateUserLocation();
      

        profile = {
          firstName: name,
          lastName: '',
          email: email,
          phone: '+1234567890',
          gender: 'Male',
          dob: '1990-01-01',
          id: userId,
          location: location,
        };

        // Set initial location label
        if (location) {
          setLocationLabel(
            location.district || location.state || location.fullAddress || 'Unknown location'
          );
        } else {
          setLocationLabel('Unknown location');
        }

        locationService.startLocationWatch(async (position) => {
          const loc = await locationService.updateUserLocation(position.coords);
          setUserProfile((prev) => prev ? { ...prev, location: loc } : prev);
          if(loc === null){
            setLocationLabel('Unknown location');
          }else{
            setLocationLabel(
              loc?.district || loc?.state || loc?.fullAddress || 'Unknown location'
            );
          }
        });

        setUserProfile(profile);
        setIsAuthorized(true);
        setIsProfileLoaded(true);
        userProfileService.generateSessionId(profile.email, profile.id);
      } else {
        // Real mode (live user)
        const profilePromise = userProfileService.fetchUserProfile();
        const locationPromise = locationService.updateUserLocation();

        const [res, locRes] = await Promise.allSettled([profilePromise, locationPromise]);

        if (res.status === 'fulfilled') {
          profile = res.value.profile;

          if (locRes.status === 'fulfilled') {
            location = locRes.value;
            profile.location = location;

            // Set initial location label
            if (location) {
              setLocationLabel(
                location.district || location.state || location.fullAddress || 'Unknown location'
              );
            } else {
              setLocationLabel('Unknown location');
            }

            locationService.startLocationWatch(async (position) => {
              const loc = await locationService.updateUserLocation(position.coords);
              setUserProfile((prev) => prev ? { ...prev, location: loc } : prev);
              if(loc === null){
                setLocationLabel('Unknown location');
              }else{
                setLocationLabel(
                  loc?.district || loc?.state || loc?.fullAddress || 'Unknown location'
                );
              }
            });
          }

          setUserProfile(profile);
          setIsAuthorized(res.value.isAuthorized);
          setIsProfileLoaded(res.value.isProfileLoaded);

          const sessionId = generateSessionId(profile.email, profile.id);
          userProfileService.setSessionId(sessionId);
          const isVoiceEnabled = await VoiceService.isVoiceEnable(profile.email);
          setIsVoiceEnabled(isVoiceEnabled);
        }
      }

      setIsProfileLoading(false);

      if (!hasSentWelcome) {
        let welcomeText = `👋 Hi ${profile.firstName}! I am  KITS AI 🤖, your travel assistant! ✈️`;
      
        const locationParts = [
          location?.city,
          location?.district,
          location?.state,
          location?.country
        ].filter(Boolean); // Remove empty values
      
        if (locationParts.length) {
          welcomeText += ` I see you're in ${locationParts.join(', ')}.`;
        }
      
        welcomeText += ` How can I help you plan your journey today? 🌍`;
      
        setMessages((prev) => [
          ...prev,
          {
            id: 'welcome-message',
            sender: 'bot',
            text: welcomeText,
            showCarousel: false,
            loading: false,
            // No adSlot for welcome message - it doesn't show ads
          },
        ]);
      
        setHasSentWelcome(true);
      }
      

      setTimeout(() => {
        if (profile) {

          userProfileService.setProfile(profile);
          const sessionId = userProfileService.getSessionId();
          LangflowApiService.sendMessage(
            'session id - ' + sessionId,
            sessionId
          );
          // LangflowApiService.sendMessage(
          //   'my personal profile -' + JSON.stringify(profile),
          //   userProfileService.getSessionId()
          // );
        }
          // LangflowApiService.sendMessage(
          //   'My current location is ' + location.fullAddress,
          //   userProfileService.getSessionId()
          // );

          // var metaData = {
          //   "requestSignature": reqSignature,
          //   "userData": userData,
          //   "token": localStorage.getItem('token'),
          //   "source": localStorage.getItem('source')
          // };
          // LangflowApiService.sendMessage(
          //   JSON.stringify(metaData),
          //   userProfileService.getSessionId()
          // );
        
      }, 0);

      // Store reqSignature and userData in localStorage if they exist
      if (reqSignature) {
        localStorage.setItem('requestSignature', reqSignature);
      }
      if (userData) {
        localStorage.setItem('userData', userData.replace(/ /g, '+'));
      }
    } catch (err) {
      console.error('Initialization error:', err);
      setIsProfileLoading(false);
    }
  };

  useEffect(() => {
    initializeLocation();
    return () => {
      locationService.stopLocationWatch();
      if (recognition) recognition.abort();
    };
  }, []);

  const handleSendMessage = async (text) => {
    // Helper utilities for sanitising error replies
    const stripUrls = (str) => typeof str === 'string' ? str.replace(/https?:\/\/\S+/gi, '') : str;
    const toFriendlyError = (str) => {
      if (typeof str !== 'string') return str;
      let msg = str.replace(/for url:.*/i, '').trim();
      if (!/\b(please|try)/i.test(msg)) {
        msg += '';
      }
      return msg;
    };
    if (!text.trim() || !isAuthorized) return;

    // Mark that user has interacted
    setHasUserInteracted(true);

    setShowOverlay(false);
    setShowMapOverlay(false);
    setShowBlogOverlay(false);

    const userMsg = {
      id: Date.now() + '-user',
      sender: 'user',
      text: text,
      loading: false,
    };

    const loadingMsg = {
      id: Date.now() + '-bot',
      sender: 'bot',
      text: '...',
      loading: true,
    };

    const newMessages = [...messages, userMsg, loadingMsg];
    setMessages(newMessages);
    setInputValue('');

    // Track index of previous completed bot messages (excluding current loading message)
    const botIndex = messages.filter((m) => m.sender === 'bot' && !m.loading && m.adProvider).length;

    const adProvider = 'imprezia';

    console.log('[Ad Handling] botIndex:', botIndex, 'adProvider:', adProvider);

    try {
      const data = await LangflowApiService.sendMessage(
        text,
        userProfileService.getSessionId()
      );
      const { text: responseText, travelFrom, travelTo, travelOptions, ktmbTimetable, departureTime } =
        LangflowApiService.parseResponse(data);

        const stripUrls = (str) => typeof str === 'string' ? str.replace(/https?:\/\/\S+/gi, '') : str;
        const sanitizedText = stripUrls(responseText);
        const isErrorLike = typeof sanitizedText === 'string' && /error|fail|server|unable|http/i.test(sanitizedText);

        console.log('[Monetization Check] isErrorLike:', isErrorLike);
        console.log('[Monetization Check] botIndex:', botIndex);
        
        const shouldMonetizeWithImprezia = !isErrorLike && adProvider === 'imprezia';
        console.log('[Monetization Check] shouldMonetizeWithImprezia:', shouldMonetizeWithImprezia);

        const botMsg = {
        id: loadingMsg.id,
        sender: 'bot',
        text: isErrorLike ? toFriendlyError(sanitizedText) : (sanitizedText || 'Unable to retrieve information at the moment. Please try again later.'),
        adProvider,
        monetizedResult: null,
        travelFrom, 
        travelTo,
        travelOptions,
        ktmbTimetable,  
        departureTime,
        loading: false,
        showCarousel: false,
        forceDisplay: isErrorLike,
      };

      const updatedMessages = newMessages.map((msg) =>
        msg.id === loadingMsg.id ? botMsg : msg
      );

      setMessages(updatedMessages);

      if (shouldMonetizeWithImprezia) {
        (async () => {
          try {
            console.log('[Imprezia] Requesting monetization for message:', loadingMsg.id);
            console.log('[Imprezia] Request:', text.substring(0, 50));
            console.log('[Imprezia] Response:', sanitizedText.substring(0, 50));
            
            const result = await Imprezia.monetize({
              request: text,
              response: sanitizedText || 'Unable to retrieve information at the moment. Please try again later.',
            });

            console.log('[Imprezia] Monetization result:', result);
            console.log('[Imprezia] Result type:', typeof result);
            console.log('[Imprezia] Result keys:', result ? Object.keys(result) : 'null');
            console.log('[Imprezia] Result.linkData:', result?.linkData);
            const hasAds = result && result.linkData && Object.keys(result.linkData).length > 0;
            console.log('[Imprezia] Result has ads:', hasAds);

            setMessages((prev) => {
              const updated = prev.map((m) => {
                if (m.id === loadingMsg.id) {
                  console.log('[Imprezia] Updating message with monetizedResult:', m.id);
                  return { ...m, monetizedResult: result };
                }
                return m;
              });
              console.log('[Imprezia] Messages updated');
              return updated;
            });
          } catch (monetizeError) {
            console.error('[Imprezia] ❌ Monetization error:', monetizeError);
          }
        })();
      }
      
      // Speak the response if voice reply is enabled
      if (voiceReplyEnabled && responseText) {
        let fullSpeechText = responseText;
        
        // Add travel options to speech if available
        if (travelOptions && Array.isArray(travelOptions) && travelOptions.length > 0) {
          const travelOptionsText = formatTravelOptionsForSpeech(travelOptions);
          if (travelOptionsText) {
            fullSpeechText += '. ' + travelOptionsText;
          }
        }
        
        speakText(fullSpeechText);
        if(travelOptions){
          console.log("travelOptions", travelOptions);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const stripUrls = (str) => typeof str === 'string' ? str.replace(/https?:\/\/\S+/gi, '') : str;
      const extractedError = (() => {
          if (typeof error === 'string') return error;
          if (error?.message) return error.message;
          if (error?.error) return error.error;
          try {
            return JSON.stringify(error);
          } catch {
            return '[Error fetching response]';
          }
        })();
        const errorMsg = {
          id: loadingMsg.id,
          sender: 'bot',
          text: toFriendlyError(stripUrls(extractedError)),
          loading: false,
          forceDisplay: true,
        };
      setMessages(
        newMessages.map((msg) =>
          msg.id === loadingMsg.id ? errorMsg : msg
        )
      );
    }
  };

  const handleInputFocus = () => {
    setTimeout(() => {
      if (chatBoxRef.current) {
        chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
      }
    }, 300);
  };

  const handleMicPress = (event) => {

    // Stop any ongoing text-to-speech when user starts speaking
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    
    // Mark that user has interacted
    setHasUserInteracted(true);
    
    // Clear previous transcript
    setInputValue('');
    setTranscript('');
    
    // Set recognition language based on the last bot message
    const langCode = conversationLanguage || 'en';
    const langCodeFull = SUPPORTED_LANGUAGES[langCode]?.code || 'en-US';
    console.log(`>>>>>> Mic pressed. Listening for ${langCode} (${langCodeFull})`);
    
    // Start Google Cloud Speech-to-Text
    googleSpeech.startListening(langCode)
      .then(success => {
        if (success) {
          setIsListening(true);
        }
      })
      .catch(error => {
        console.error('Error starting Google speech recognition:', error);
        setIsListening(false);
        
        // Check if we're in WebView
        if (googleSpeech.isWebView) {
          // Show a message for WebView users
          setTranscript('Speech recognition may not work in this browser. Tap the mic again or type your message.');
          setTimeout(() => {
            if (!isListening) setTranscript('');
          }, 4000);
        }
      });
  };

  // Handle mic button release (stop recording and send)
  const handleMicRelease = (event) => {

    // Only stop if we're actually listening
    if (!isListening) {
      console.log('Not currently listening, ignoring release');
      return;
    }
    
    console.log('Stopping Google speech recognition');
    
    // Stop Google Cloud Speech-to-Text
    googleSpeech.stopListening()
      .then(result => {
        if (result.transcript) {
          setTranscript(result.transcript);
          setInputValue(result.transcript);
          
          // Update conversation language if detected
          if (result.language) {
            setConversationLanguage(result.language);
            console.log(`Language detected: ${result.language}`);
          }
          
          // Auto-send the message if we have a transcript
          if (result.transcript.trim()) {
            handleSendMessage(result.transcript);
            setTranscript('');
          }
        } else if (result.webViewFallback) {
          // Handle WebView fallback - focus on the input field
          if (inputRef.current) {
            inputRef.current.focus();
            setTranscript('Please type your message');
            setTimeout(() => setTranscript(''), 2000);
          }
        }
        setIsListening(false);
      })
      .catch(error => {
        console.error('Error stopping Google speech recognition:', error);
        setIsListening(false);
      });
  };

  useEffect(() => {
    setIsListening(googleSpeech.isListening);
    
    if (googleSpeech.transcript && !transcript) {
      setTranscript(googleSpeech.transcript);
      setInputValue(googleSpeech.transcript);
    }
    
    if (googleSpeech.recognizedLanguage && googleSpeech.recognizedLanguage !== conversationLanguage) {
      setConversationLanguage(googleSpeech.recognizedLanguage);
    }
    
    if (googleSpeech.error) {
      console.error('Google Speech-to-Text error:', googleSpeech.error);
    }
  }, [googleSpeech.isListening, googleSpeech.transcript, googleSpeech.recognizedLanguage, googleSpeech.error]);

  // Initialize speech synthesis
  useEffect(() => {
    // For Android WebView, we'll use audio elements instead of speechSynthesis
    if (isAndroidWebView) {
      console.log('Android WebView detected - enabling audio element TTS fallback');
      setSpeechSynthesisSupported(true);
      setHasUserInteracted(true);
      
      // Create audio context to unlock audio
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          const audioContext = new AudioContext();
          // Create and play a silent sound to unlock audio
          const buffer = audioContext.createBuffer(1, 1, 22050);
          const source = audioContext.createBufferSource();
          source.buffer = buffer;
          source.connect(audioContext.destination);
          source.start(0);
          console.log('Audio context initialized');
        }
      } catch (e) {
        console.warn('Could not initialize AudioContext:', e);
      }
      
      // Pre-create an audio element for TTS
      if (!document.getElementById('tts-audio-element')) {
        const audioElement = document.createElement('audio');
        audioElement.id = 'tts-audio-element';
        audioElement.style.display = 'none';
        audioElement.onended = () => setIsSpeaking(false);
        audioElement.onpause = () => setIsSpeaking(false);
        audioElement.onerror = (e) => {
          console.error('Audio element error:', e);
          setIsSpeaking(false);
        };
        document.body.appendChild(audioElement);
        console.log('TTS audio element created');
      }
      
      return;
    }
    
    // For non-WebView browsers, check normal support
    const supported = 'speechSynthesis' in window;
    setSpeechSynthesisSupported(supported);
    console.log('Speech synthesis supported:', supported);
    
    if (!supported) {
      console.error('Speech synthesis not supported in this browser');
      return;
    }
    
    // Try to load voices
    try {
      // Load voices immediately if available
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        console.log(`Loaded ${voices.length} voices`);
      } else {
        console.warn('No voices available initially');
      }
      
      // Set up event listener for voices loaded
      window.speechSynthesis.onvoiceschanged = () => {
        const availableVoices = window.speechSynthesis.getVoices();
        console.log(`Voices changed: ${availableVoices.length} voices available`);
        if (availableVoices.length > 0) {
          setSpeechSynthesisSupported(true);
        }
      };
      
      // For Android WebView, test if speech synthesis actually works
      if (isAndroidWebView) {
        // Create a silent test utterance
        const testUtterance = new SpeechSynthesisUtterance('test');
        testUtterance.volume = 0; // Silent test
        testUtterance.onend = () => {
          console.log('Speech synthesis works in Android WebView');
          setSpeechSynthesisSupported(true);
        };
        testUtterance.onerror = (err) => {
          console.error('Speech synthesis failed in Android WebView:', err);
          setSpeechSynthesisSupported(false);
        };
        window.speechSynthesis.speak(testUtterance);
      }
      
      // Force a check for voices after a delay
      setTimeout(() => {
        const delayedVoices = window.speechSynthesis.getVoices();
        console.log(`Delayed voice check: ${delayedVoices.length} voices`);
        if (delayedVoices.length > 0) {
          setSpeechSynthesisSupported(true);
        } else if (isAndroidWebView) {
          console.warn('No voices available after delay in Android WebView');
          // Try one more time with a visible utterance
          try {
            const visibleTest = new SpeechSynthesisUtterance('Testing speech');
            visibleTest.onend = () => setSpeechSynthesisSupported(true);
            visibleTest.onerror = (e) => console.error('Visible test failed:', e);
            window.speechSynthesis.speak(visibleTest);
          } catch (e) {
            console.error('Error in visible test:', e);
          }
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error loading voices:', error);
      setSpeechSynthesisSupported(false);
    }
    
    // Handle user interaction to unlock audio
    const markInteraction = () => {
      setHasUserInteracted(true);
      console.log('User interaction detected');
    };
    
    // Add event listeners for user interaction
    document.addEventListener('click', markInteraction);
    document.addEventListener('touchstart', markInteraction);
    
    return () => {
      // Clean up event listeners
      document.removeEventListener('click', markInteraction);
      document.removeEventListener('touchstart', markInteraction);
      
      // Clean up speech synthesis event listener
      if (supported) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  // Function to speak text using premium female voices
  // Utility function to remove emojis from text
  const removeEmojis = (str) => str.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDD00-\uDDFF])/g, '');

  const speakText = (text) => {
    if (!text?.trim()) return;
    
    // Set speaking state
    setIsSpeaking(true);
    
    // Check for user interaction first
    if (!hasUserInteracted) {
      console.log('User has not interacted yet, setting flag');
      setHasUserInteracted(true);
    }
    
    try {
      // Use our VoiceHelper with premium male voices (excluding Brian)
      console.log('Using premium male voices from StreamElements (excluding Brian)');
      
      // Create audio element with event handlers for state management
      let audioElement = document.getElementById('tts-audio-element');
      if (!audioElement) {
        audioElement = document.createElement('audio');
        audioElement.id = 'tts-audio-element';
        audioElement.style.display = 'none';
        audioElement.onended = () => setIsSpeaking(false);
        audioElement.onpause = () => setIsSpeaking(false);
        audioElement.onerror = () => {
          console.error('Audio element error');
          setIsSpeaking(false);
        };
        document.body.appendChild(audioElement);
      }
      
      // Detect language of bot's own message to set expectation for user's reply
      const detectedLanguage = VoiceHelper.detectLanguage(text);
      setConversationLanguage(detectedLanguage);
      console.log(`>>>>>> Bot speaking in ${detectedLanguage}. Recognizer will now listen for ${SUPPORTED_LANGUAGES[detectedLanguage]?.code}.`);

      // Remove emojis before speaking
      const sanitizedText = removeEmojis(text);
      // Use the VoiceHelper to speak with automatic language detection
      VoiceHelper.speakText(sanitizedText);
      
      // The VoiceHelper will handle all the complexity of selecting voices
      // and falling back if needed
    } catch (error) {
      console.error('Error speaking text:', error);
      // Silent failure - no visual error message
      setIsSpeaking(false);
    }
  };
  
  // Toggle voice reply
  const toggleVoiceReply = () => {
    // Mark that user has interacted
    setHasUserInteracted(true);
    
    // Check if we're in Android WebView
    const isAndroidWebView = (/wv/.test(navigator.userAgent) && /android/i.test(navigator.userAgent)) || 
                           (/Android/.test(navigator.userAgent) && /Version\//.test(navigator.userAgent)) ||
                           (/Android/.test(navigator.userAgent) && /Chrome\/[0-9]/.test(navigator.userAgent));
    
    // Special handling for Android WebView
    if (isAndroidWebView) {
      console.log('Android WebView detected in toggle function - using special handling');
      
      // Toggle the state directly for WebView
      const newVoiceReplyEnabled = !voiceReplyEnabled;
      setVoiceReplyEnabled(newVoiceReplyEnabled);
      
      // No confirmation message - silent operation
      
      return;
    }
    
    // For non-WebView browsers, continue with normal flow silently
    
    // Check if speech synthesis is available
    if (!window.speechSynthesis) {
      console.error('Speech synthesis is not available on your device');
      // Silent failure - no visual error
      return;
    }
    
    // Check if voices are available
    const voices = window.speechSynthesis.getVoices();
    if (!voices || voices.length === 0) {
      console.error('No voices available');
      
      // Try to force load voices silently
      try {
        window.speechSynthesis.onvoiceschanged = () => {
          const newVoices = window.speechSynthesis.getVoices();
          console.log(`Voices loaded after error: ${newVoices.length}`);
        };
      } catch (e) {
        console.error('Error setting onvoiceschanged:', e);
      }
      return;
    }
    
    // Toggle the state
    const newVoiceReplyEnabled = !voiceReplyEnabled;
    setVoiceReplyEnabled(newVoiceReplyEnabled);
    
    // Cancel any ongoing speech
    try {
      window.speechSynthesis.cancel();
    } catch (e) {
      console.error('Error canceling speech:', e);
    }
    
    // If enabling voice reply
    if (newVoiceReplyEnabled) {
      console.log('Enabling voice reply');
      
      try {
        // Create a confirmation message but don't show any visual feedback
        const testUtterance = new SpeechSynthesisUtterance('Voice reply enabled');
        
        // After confirmation, speak the last bot message if available
        testUtterance.onend = () => {
          console.log('Confirmation message spoken');
          setTimeout(() => {
            const lastBotMessage = messages.filter(msg => msg.sender === 'bot' && !msg.loading).pop();
            if (lastBotMessage?.text) {
              console.log('Speaking last bot message:', lastBotMessage.text.substring(0, 50) + '...');
              speakText(lastBotMessage.text);
            }
          }, 500);
        };
        
        // Handle errors silently
        testUtterance.onerror = (error) => {
          console.error('Error in confirmation speech:', error);
          setIsSpeaking(false);
        };
        
        // Speak the confirmation
        console.log('Speaking confirmation message');
        window.speechSynthesis.speak(testUtterance);
      } catch (error) {
        console.error('Error during speech synthesis:', error);
        setIsSpeaking(false);
      }
    } else {
      console.log('Voice reply disabled');
      // No visual feedback when disabling
    }
  };
  
  // Convert travel options to readable string
  const formatTravelOptionsForSpeech = (travelOptions) => {
    if (!travelOptions || !Array.isArray(travelOptions) || travelOptions.length === 0) {
      return '';
    }

    const options = travelOptions.map((option, index) => {
      const { travelOptions: mode, duration, distance, fare } = option;
      
      let modeText = mode;
      if (mode === 'drive') modeText = 'car';
      if (mode === 'motorbike') modeText = 'motorcycle';
      
      let fareText = '';
      if (fare && fare !== 'RM0-0') {
        fareText = `, costing ${fare}`;
      }
      
      return `${modeText}: ${duration}, ${distance}${fareText}`;
    });

    if (options.length === 1) {
      return `Travel option: ${options[0]}`;
    } else {
      const lastOption = options.pop();
      return `Travel options: ${options.join(', ')}, and ${lastOption}`;
    }
  };

  const [inputValue, setInputValue] = useState('');

  // Use useLayoutEffect for immediate scroll after render
  useLayoutEffect(() => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    // Show popup initially after 2s, then every 30s
    const showPopup = () => {
      setShowTimetablePopup(true);
      if (timetablePopupTimeout.current) clearTimeout(timetablePopupTimeout.current);
      timetablePopupTimeout.current = setTimeout(() => setShowTimetablePopup(false), 10000);
    };
    const initialTimeout = setTimeout(showPopup, 2000);
    timetablePopupInterval.current = setInterval(showPopup, 30000);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(timetablePopupInterval.current);
      clearTimeout(timetablePopupTimeout.current);
    };
  }, []);

  return (
    <div className="chatbot-container">
      <div className="fab-container">
      {/* {isAuthorized && (
        <div style={{ position: 'relative', width: '64px', height: '64px' }}>
          <button
            className="fab-action-btn timetable-btn"
            onClick={() => setShowKtmbTimetable(true)}
            title="Go to KTMB Timetable"
            style={{ zIndex: 1100 }}
          >
            <svg className="train-svg-icon" width="28" height="28" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="16" y="12" width="32" height="32" rx="8" fill="#1976D2"/>
              <rect x="20" y="16" width="24" height="16" rx="4" fill="#fff"/>
              <rect x="24" y="36" width="16" height="6" rx="3" fill="#FFD600"/>
              <circle cx="24" cy="48" r="4" fill="#424242" stroke="#fff" strokeWidth="2"/>
              <circle cx="40" cy="48" r="4" fill="#424242" stroke="#fff" strokeWidth="2"/>
              <rect x="28" y="22" width="8" height="6" rx="2" fill="#1976D2"/>
              <rect x="18" y="44" width="28" height="4" rx="2" fill="#4CAF50"/>
            </svg>
          </button>
          {showTimetablePopup && (
            <div className="timetable-popup timetable-popup-animated" onClick={() => setShowTimetablePopup(false)}>
              <span className="timetable-popup-robot">
                <FontAwesomeIcon icon={faRobot} />
              </span>
              <span className="timetable-popup-text">
                <span className="timetable-popup-greeting">Hello!</span>
                <span className="timetable-popup-agent">I am your <b>KTMB AI Agent</b> <span className="timetable-popup-badge">✨</span></span>
              </span>
              <span className="timetable-popup-close" title="Close">×</span>
            </div>
           )}
        </div>
        )} */}
        {isVoiceEnabled && (
        <button
          className={`fab-action-btn${voiceReplyEnabled ? ' active' : ''}`}
          onClick={toggleVoiceReply}
          title={voiceReplyEnabled ? 'Disable Voice Reply' : (speechSynthesisSupported ? 'Enable Voice Reply' : 'Voice Reply Not Supported')}
          disabled={!speechSynthesisSupported}
        >
          <FontAwesomeIcon icon={voiceReplyEnabled ? faVolumeUp : faVolumeMute} className="fab-speaker-icon" />
        </button>
        )}
      </div>
      <div className="chatbot-scrollable">
        {/* Greeting and Location */}
        <div className="greeting-section">
          <div className="greeting-name">Hello, {userProfile?.firstName || 'Loading...'}</div>
          <div className="greeting-help">How can I help?</div>
          <div className="greeting-location">
            <span className="location-icon"><FontAwesomeIcon icon={faLocationDot} /></span>
            {locationLabel || 'Unknown location'}
          </div>
        </div>
        <KtmbDelays />

        {/* Quick Action Buttons */}
        <div className="quick-actions">
          <button
            className='quick-action-button'
            onClick={() => {
              setHasUserInteracted(true);
              setInputValue('Help me plan a trip from [origin] to [destination]');
              setSuggestedText('Help me plan a trip from [origin] to [destination]');
            }}
          >
            Plan Route
          </button>
          <button
            className='quick-action-button'
            onClick={() => {
              setHasUserInteracted(true);
              setInputValue('What types of KTMB train tickets and classes are available?');
              setSuggestedText('What types of KTMB train tickets and classes are available?');
            }}
          >
            Ticket Types
          </button>
          <button
            className='quick-action-button'
            onClick={() => {
              setHasUserInteracted(true);
              setInputValue('How can I book a KTMB train ticket?');
              setSuggestedText('How can I book a KTMB train ticket?');
            }}
          >
            Book Ticket
          </button>
          <button
            className='quick-action-button'
            onClick={() => {
              setHasUserInteracted(true);
              setInputValue('How are KTMB ticket fares calculated?');
              setSuggestedText('How are KTMB ticket fares calculated?');
            }}
          >
            Ticket Fare
          </button>
          <button
            className='quick-action-button'
            onClick={() => {
              setHasUserInteracted(true);
              setInputValue('How can I locate KTMB stations?');
              setSuggestedText('How can I locate KTMB stations?');
            }}
          >
            KTMB Stations
          </button>
          <button
            className='quick-action-button'
            onClick={() => {
              setHasUserInteracted(true);
              setInputValue('What can I do using the KTMB mobile app?');
              setSuggestedText('What can I do using the KTMB mobile app?');
            }}
          >
            Mobile App
          </button>
          <button
            className='quick-action-button'
            onClick={() => {
              setHasUserInteracted(true);
              setInputValue('How do KTMB points and rewards work?');
              setSuggestedText('How do KTMB points and rewards work?');
            }}
          >
            Rewards
          </button>
          <button
            className='quick-action-button'
            onClick={() => {
              setHasUserInteracted(true);
              setInputValue('What is the policy for refunds?');
              setSuggestedText('What is the policy for refunds?');
            }}
          >
            Refunds
          </button>
        </div>
        <div ref={chatBoxRef} className="chatbox">
          {messages.some((msg) => msg.showCarousel) && (
            <div className="carousel-wrapper">
              <Carousel userLocation={userProfile?.location} />
            </div>
          )}

          {!isProfileLoading && (!isAuthorized || !isProfileLoaded) && (
            <div className="auth-alert">
              <FontAwesomeIcon icon={faTriangleExclamation} className="alert-icon" />
              {/* <span>You are not authenticated. Please check your authentication and try again.</span> */}
              
              <div className="platform-usage-demo" style={{ marginTop: '20px', width: '100%' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#333' }}>
                  Platform Usage Demonstration
                </h3>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px', lineHeight: '1.5' }}>
                  In many cases, users interact with visitmalaysia.ai through embedded chatbot interfaces within partner applications as part of their journey planning experience.
                </p>
                <div style={{ position: 'relative', paddingBottom: '177.78%', height: 0, overflow: 'hidden', maxWidth: '100%', background: '#000', borderRadius: '8px' }}>
                  <iframe
                    src="https://www.youtube.com/embed/954Zs9N8W1s"
                    title="VisitMalaysia.ai Platform Usage Demo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  ></iframe>
                </div>
                <p style={{ fontSize: '12px', color: '#888', marginTop: '8px', fontStyle: 'italic', lineHeight: '1.4' }}>
                  Demonstration of the VisitMalaysia.ai assistant powering the official Malaysia Railway (KTMB) mobile app, providing real-time transit and travel guidance to over 300,000 users.
                </p>
              </div>
            </div>
          )}

          {(() => {
            const elements = [];
            let adIdx = 0;
            const filteredMessages = messages.filter(msg => msg.forceDisplay || !isWebViewErrorMessage(msg));
            
            filteredMessages.forEach((msg, msgIndex) => {
              // Check if Imprezia returned ads (linkData object with keys)
              const hasImpreziaAds = msg.monetizedResult && msg.monetizedResult.linkData && Object.keys(msg.monetizedResult.linkData).length > 0;
              
              elements.push(
                <ChatMessage
              key={msg.id}
              sender={msg.sender}
              text={msg.text}
              loading={msg.loading}
              travelFrom={msg.travelFrom}
              travelTo={msg.travelTo}
              travelOptions={msg.travelOptions}
              ktmbTimetable={msg.ktmbTimetable}
              departureTime={msg.departureTime}
              hasImpreziaAd={hasImpreziaAds}
              showOverlay={showOverlay}
              setShowOverlay={setShowOverlay}
              showMapOverlay={showMapOverlay}
              setShowMapOverlay={setShowMapOverlay}
              showBlogOverlay={showBlogOverlay}
              setShowBlogOverlay={setShowBlogOverlay}
                />
              );

              if (msg.sender === 'bot') {
                console.log('[Render] Bot message:', msg.id);

                if (msg.adProvider === 'imprezia') {
                  const hasImpreziaAds = msg.monetizedResult && msg.monetizedResult.linkData && Object.keys(msg.monetizedResult.linkData).length > 0;
                  console.log('[Render] Imprezia provider. hasImpreziaAds:', hasImpreziaAds);
                  if (hasImpreziaAds) {
                    // Prefer Imprezia when ads are available
                    elements.push(
                      <ImpreziaAdBanner 
                        key={`imprezia-ad-${msg.id}`}
                        monetizedResult={msg.monetizedResult}
                        messageId={msg.id}
                      />
                    );
                  }
                }
              }
            });
            return elements;
          })()}
          <div ref={endOfMessagesRef} />
        </div>

        {/* Suggested Text Display */}
        

          {/* {suggestedText && (
          <div className="suggested-text-container">
            <span>{suggestedText}</span>
            <button
              className="suggested-text-clear-btn"
              onClick={() => setSuggestedText('')}
              title="Clear suggestion"
            >
              ×
            </button>
          </div>
        )} */}
      </div>
      <div className="input-container">
        {/* Error messages are disabled */}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (suggestedText && e.target.value !== suggestedText) setSuggestedText('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage(inputValue)}
          onFocus={handleInputFocus}
          placeholder={
            isProfileLoading
              ? 'Loading...'
              : isAuthorized
              ? 'Type your message...'
              : 'Please authenticate to chat'
          }
          className="chat-input"
          disabled={!isAuthorized || isProfileLoading}
        />
        {(isVoiceEnabled && isSpeechRecognitionSupported) && (
          <button
            onMouseDown={handleMicPress}
            onMouseUp={handleMicRelease}
            onTouchStart={handleMicPress}
            onTouchEnd={handleMicRelease}
            className={`mic-button${isListening ? ' listening' : ''}`}
            disabled={!isSpeechRecognitionSupported || !isAuthorized || isProfileLoading}
            title={isListening ? 'Stop Recording' : 'Press and hold to speak'}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1v14M8 5v6a4 4 0 0 0 8 0V5"></path><rect x="9" y="1" width="6" height="14" rx="3"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/></svg>
          </button>
        )}
        <button
          onClick={() => {
            setHasUserInteracted(true);
            handleSendMessage(inputValue);
          }}
          className="send-button"
          disabled={!inputValue.trim() || !isAuthorized || isProfileLoading}
        >
          {isProfileLoading ? (
            <FontAwesomeIcon icon={faSpinner} className="loading-spinner" />
          ) : (
            <FontAwesomeIcon icon={faArrowUp} />
          )}
        </button>
      </div>

      {transcript && (
        <div className="transcript-text">{transcript}</div>
      )}

      {showKtmbTimetable && (
        <div className="ktmb-timetable-modal-overlay" onClick={() => setShowKtmbTimetable(false)}>
          <div className="ktmb-timetable-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ktmb-timetable-modal-close" onClick={() => setShowKtmbTimetable(false)}>&times;</button>
            <KtmbAiAgent ktmbDelayInfo={ktmbDelayInfo}/>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;